import React from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

/**
 * PremiumDialog - A reusable dialog component with premium styling
 * @param {Object} props
 * @param {boolean} props.open - Control dialog visibility
 * @param {function} props.onClose - Close handler
 * @param {string} props.title - Dialog title
 * @param {React.ReactNode} props.icon - Icon component for header
 * @param {React.ReactNode} props.children - Dialog content
 * @param {string} props.maxWidth - Dialog max width: 'xs', 'sm', 'md', 'lg', 'xl'
 * @param {boolean} props.fullWidth - Full width dialog
 * @param {string} props.headerGradient - Header gradient color
 */
const PremiumDialog = ({
  open,
  onClose,
  title,
  icon: Icon,
  children,
  maxWidth = "md",
  fullWidth = true,
  headerGradient = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  ...rest
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "20px",
          backgroundColor: "#fff",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
          overflow: "hidden",
        },
      }}
      {...rest}
    >
      {/* Premium Header */}
      <ArgonBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={3}
        sx={{
          background: headerGradient,
          borderBottom: "2px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <ArgonBox display="flex" alignItems="center" gap={2}>
          {Icon && (
            <ArgonBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 48,
                height: 48,
                borderRadius: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Icon sx={{ fontSize: 28, color: "#fff" }} />
            </ArgonBox>
          )}
          <ArgonTypography
            variant="h5"
            sx={{
              color: "#fff",
              fontWeight: 700,
              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.2)",
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </ArgonTypography>
        </ArgonBox>

        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(10px)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              transform: "rotate(90deg)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <Close sx={{ fontSize: 24 }} />
        </IconButton>
      </ArgonBox>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>{children}</DialogContent>
    </Dialog>
  );
};

PremiumDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOf(["xs", "sm", "md", "lg", "xl"]),
  fullWidth: PropTypes.bool,
  headerGradient: PropTypes.string,
};

export default PremiumDialog;
